import React, { useState, useCallback, useEffect, useMemo } from 'react';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import {
  getMentorDayAttendance, setMentorAttendance,
  markMentorLeave, markInstituteLeave, cancelInstituteLeave,
  getAttendanceAnalytics,
  AttendanceMentor, AttendanceAnalytics, AnalyticsDay, AnalyticsMentor, StaffOnLeave,
} from '../../lib/api';
import { toast } from 'react-toastify';

/* ── CSS ────────────────────────────────────────────────────────────────── */
const CSS = `
@keyframes atFadeUp {
  from { opacity:0; transform:translateY(18px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes atSlideDown {
  from { opacity:0; transform:translateY(-6px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes atShimmer {
  from { transform:translateX(-100%); }
  to   { transform:translateX(220%); }
}
@keyframes atPulse {
  0%,100% { opacity:1; }
  50%     { opacity:.45; }
}
@keyframes atBannerSlide {
  from { opacity:0; transform:translateY(-10px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes atBarGrow {
  from { width:0; }
  to   { }
}
@keyframes atCountUp {
  from { opacity:0; transform:scale(.7); }
  to   { opacity:1; transform:scale(1); }
}
@keyframes atRipple {
  0%   { box-shadow:0 0 0 0 rgba(22,163,74,.35); }
  70%  { box-shadow:0 0 0 10px rgba(22,163,74,0); }
  100% { box-shadow:0 0 0 0 rgba(22,163,74,0); }
}
.at-card {
  animation: atFadeUp .32s cubic-bezier(.22,.68,0,1.2) both;
  transition: box-shadow .2s ease, transform .2s ease;
}
.at-card:hover { transform:translateY(-2px); box-shadow:0 8px 28px rgba(0,0,0,.1); }
.at-skel { position:relative; overflow:hidden; background:#f1f5f9; border-radius:16px; }
.at-skel::after {
  content:'';
  position:absolute;
  inset:0;
  background:linear-gradient(90deg,transparent 0%,rgba(255,255,255,.6) 50%,transparent 100%);
  animation:atShimmer 1.5s ease infinite;
}
.at-warn { animation: atPulse 2s ease-in-out infinite; }
.at-banner { animation: atBannerSlide .3s ease both; }
.at-expand { animation: atSlideDown .22s ease both; }
.at-bar { animation: atBarGrow .7s cubic-bezier(.22,.68,0,1.1) both; }
.at-count { animation: atCountUp .4s cubic-bezier(.34,1.56,.64,1) both; }
.at-present-ring { animation: atRipple 2.5s ease-in-out infinite; }
.at-toggle-btn {
  transition: background .18s ease, color .18s ease, transform .1s ease;
}
.at-toggle-btn:active:not(:disabled) { transform: scale(.92); }
`;

/* ── Helpers ────────────────────────────────────────────────────────────── */
function todayIST() {
  return new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
function shiftDate(d: string, n: number) {
  const dt = new Date(d + 'T00:00:00Z');
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}
function fmtShort(d: string) {
  return new Date(d + 'T00:00:00Z').toLocaleDateString('en-IN', {
    weekday:'short', day:'numeric', month:'short', timeZone:'UTC',
  });
}
function fmtLong(d: string) {
  return new Date(d + 'T00:00:00Z').toLocaleDateString('en-IN', {
    weekday:'long', day:'numeric', month:'long', year:'numeric', timeZone:'UTC',
  });
}
function slotTo12(s: string) {
  if (!s) return '—';
  const [h, m] = s.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}
const PAL: [string, string][] = [
  ['#312741','#ffe500'], ['#1e3a8a','#93c5fd'], ['#5b21b6','#c4b5fd'],
  ['#064e3b','#6ee7b7'], ['#78350f','#fde68a'], ['#881337','#fda4af'],
  ['#1e3a8a','#bfdbfe'], ['#7c2d12','#fed7aa'], ['#134e4a','#99f6e4'],
  ['#312e81','#c7d2fe'],
];
function pal(id: string): [string, string] {
  const i = id.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % PAL.length;
  return PAL[i];
}
function ini(name: string) {
  return (name || '').split(' ').map(n => n[0] || '').join('').toUpperCase().slice(0, 2);
}
function pct(n: number, d: number) {
  return d === 0 ? 0 : Math.round((n / d) * 100);
}

/* ── Status config ──────────────────────────────────────────────────────── */
type St = 'Present' | 'Absent' | 'MentorLeave';
const S: Record<St, {
  label: string; short: string; icon: string;
  color: string; bg: string; stripe: string; softBg: string; textLight: string;
}> = {
  Present:     { label:'Present',   short:'P', icon:'✓', color:'#16a34a', bg:'#dcfce7', stripe:'#16a34a', softBg:'#f0fdf4', textLight:'#86efac' },
  Absent:      { label:'Absent',    short:'A', icon:'○', color:'#6b7280', bg:'#f3f4f6', stripe:'#d1d5db', softBg:'#f9fafb', textLight:'#d1d5db' },
  MentorLeave: { label:'On Leave',  short:'L', icon:'~', color:'#d97706', bg:'#fef3c7', stripe:'#fbbf24', softBg:'#fffbeb', textLight:'#fde68a' },
};

/* ── Approved leave (Admin → Leave Requests) ────────────────────────────── */
function LeaveRoleBadge({ s }: { s: StaffOnLeave }) {
  const label = s.employeeType === 'sales' ? 'Sales' : s.role === 'Student Coordinator' ? 'Coordinator' : 'Mentor';
  return (
    <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full"
      style={s.employeeType === 'sales' ? { background:'#dbeafe', color:'#1d4ed8' } : { background:'#ede9fe', color:'#5b21b6' }}>
      {label}
    </span>
  );
}

// Everyone whose leave request covering this day was approved — salespeople
// and half days too, which have no P / A / L status of their own here.
function StaffOnLeaveStrip({ staff, date }: { staff: StaffOnLeave[]; date: string }) {
  return (
    <div className="at-banner rounded-2xl px-4 py-3.5"
      style={{ background:'linear-gradient(135deg,#fffbeb,#fef3c7)', border:'1.5px solid #fde68a' }}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <p className="font-black text-amber-800 text-sm">🌴 On approved leave · {fmtShort(date)}</p>
        <p className="text-[10px] text-amber-600">From Leave Requests</p>
      </div>
      <div className="flex flex-wrap gap-2 mt-2.5">
        {staff.map(s => (
          <span key={`${s.employeeType}:${s.employeeId}`}
            className="inline-flex items-center gap-1.5 bg-white rounded-full pl-1 pr-2.5 py-1 text-xs font-semibold text-gray-700 border border-amber-200">
            <LeaveRoleBadge s={s} />
            {s.name}
            {s.halfDay && <span className="text-[10px] font-black text-amber-700">½ day</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Donut-style stat ring ───────────────────────────────────────────────── */
function DonutStat({ val, total, color, size=56 }: { val: number; total: number; color: string; size?: number }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const filled = total > 0 ? (val / total) * circ : 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform:'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={6} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={6}
        strokeDasharray={`${filled} ${circ}`} strokeLinecap="round"
        style={{ transition:'stroke-dasharray .7s cubic-bezier(.22,.68,0,1.1)' }} />
    </svg>
  );
}

/* ── Header Stat Block ───────────────────────────────────────────────────── */
function HeaderStat({ val, label, icon, sub }: { val: number; label: string; icon: string; sub?: string }) {
  return (
    <div className="flex flex-col items-center px-3 py-2 rounded-xl" style={{ background:'rgba(255,255,255,.1)' }}>
      <span className="text-base leading-none mb-0.5">{icon}</span>
      <p className="text-white font-black text-xl leading-none at-count">{val}</p>
      <p className="text-white/60 text-[10px] font-medium mt-0.5 whitespace-nowrap">{label}</p>
      {sub && <p className="text-white/40 text-[9px]">{sub}</p>}
    </div>
  );
}

/* ── Status Toggle ───────────────────────────────────────────────────────── */
function StatusToggle({ status, onChange, disabled }: {
  status: St; onChange: (s: St) => void; disabled?: boolean;
}) {
  const opts: { key: St; short: string; label: string; activeColor: string }[] = [
    { key:'Present',     short:'P', label:'Present',  activeColor:'#16a34a' },
    { key:'Absent',      short:'A', label:'Absent',   activeColor:'#6b7280' },
    { key:'MentorLeave', short:'L', label:'Leave',    activeColor:'#d97706' },
  ];
  return (
    <div className="flex rounded-lg overflow-hidden border border-gray-200 flex-shrink-0" style={{ fontSize:10 }}>
      {opts.map(opt => {
        const active = status === opt.key;
        return (
          <button key={opt.key} onClick={() => !active && !disabled && onChange(opt.key)}
            disabled={disabled}
            className="at-toggle-btn px-2.5 py-1.5 font-black leading-none select-none"
            title={opt.label}
            style={{
              background: active ? opt.activeColor : '#f8fafc',
              color: active ? '#fff' : '#94a3b8',
              cursor: disabled ? 'not-allowed' : active ? 'default' : 'pointer',
              opacity: disabled ? .6 : 1,
              minWidth: 28,
            }}>
            {opt.short}
          </button>
        );
      })}
    </div>
  );
}

/* ── Mentor Detail Card ──────────────────────────────────────────────────── */
function MentorCard({
  m, idx, busy, instLeave, isFuture, expanded, onToggle, onExpand,
}: {
  m: AttendanceMentor; idx: number; busy: boolean; instLeave: boolean;
  isFuture: boolean; expanded: boolean; onToggle: (s: St) => void; onExpand: () => void;
}) {
  const [bgCol, accentCol] = pal(m.mentorId);
  const cfg    = S[m.status as St] ?? S.Absent;
  const logged = m.scheduledCount - m.unloggedStudents.length;
  const logPct = pct(logged, m.scheduledCount);
  const hasWarn = m.status === 'Present' && m.unloggedStudents.length > 0;
  const allLogged = m.status === 'Present' && m.scheduledCount > 0 && m.unloggedStudents.length === 0;

  return (
    <div className="at-card rounded-2xl overflow-hidden bg-white"
      style={{
        animationDelay: `${idx * 45}ms`,
        border: `1.5px solid ${cfg.stripe}22`,
        borderLeft: `4px solid ${cfg.stripe}`,
        boxShadow: `0 2px 8px rgba(0,0,0,.05), 0 0 0 0 ${cfg.stripe}`,
      }}>

      {/* ── TOP ROW: avatar · info · status toggle ── */}
      <div className="flex items-start gap-3 px-4 pt-4 pb-3">

        {/* Avatar with status ring */}
        <div className="relative flex-shrink-0">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center text-sm font-black select-none"
            style={{ background:`linear-gradient(135deg,${bgCol},${bgCol}dd)`, color: accentCol, letterSpacing:'.5px' }}>
            {ini(m.mentorName)}
          </div>
          {/* Status dot */}
          <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white"
            style={{ background: cfg.color }} />
        </div>

        {/* Name + subtitle */}
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-bold text-gray-800 text-sm leading-tight truncate">{m.mentorName}</p>
            {allLogged && (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0"
                style={{ background:'#dcfce7', color:'#15803d' }}>✓ All Logged</span>
            )}
            {hasWarn && (
              <span className="at-warn text-[9px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0"
                style={{ background:'#fff7ed', color:'#c2410c' }}>⚠ {m.unloggedStudents.length} unlogged</span>
            )}
          </div>
          {/* Status badge row */}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: cfg.bg, color: cfg.color }}>
              {cfg.icon} {cfg.label}
            </span>
            <span className="text-[10px] text-gray-400">
              {m.scheduledCount} student{m.scheduledCount !== 1 ? 's' : ''}
            </span>
            {m.approvedLeave && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background:'#fffbeb', color:'#b45309', border:'1px solid #fde68a' }}>
                🌴 {m.approvedLeave.halfDay ? '½ day leave' : 'Approved leave'}
              </span>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {!isFuture && !instLeave && (
            <StatusToggle status={m.status as St} onChange={onToggle} disabled={busy} />
          )}
          {instLeave && (
            <span className="text-[10px] font-bold px-2 py-1 rounded-lg"
              style={{ background:'#fef2f2', color:'#dc2626' }}>Holiday</span>
          )}
          {isFuture && (
            <span className="text-[10px] text-gray-400">—</span>
          )}
        </div>
      </div>

      {/* ── LOG PROGRESS BAR ── (only when Present + has students) */}
      {m.status === 'Present' && m.scheduledCount > 0 && (
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Class Logs</p>
            <p className="text-[10px] font-black" style={{ color: hasWarn ? '#c2410c' : '#16a34a' }}>
              {logged}/{m.scheduledCount} logged
            </p>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="at-bar h-full rounded-full transition-all"
              style={{
                width: `${logPct}%`,
                background: hasWarn
                  ? 'linear-gradient(90deg,#f97316,#ef4444)'
                  : 'linear-gradient(90deg,#16a34a,#4ade80)',
                animationDelay: `${idx * 45 + 200}ms`,
              }} />
          </div>
          {/* Student count row */}
          <div className="flex items-center gap-3 mt-2">
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background:'#16a34a' }} />
              <p className="text-[10px] text-gray-500">{logged} logged</p>
            </div>
            {m.unloggedStudents.length > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background:'#f97316' }} />
                <p className="text-[10px] text-gray-500">{m.unloggedStudents.length} pending</p>
              </div>
            )}
            {m.unloggedStudents.length > 0 && (
              <button onClick={onExpand}
                className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-md transition-all hover:opacity-80"
                style={{ background:'#fff7ed', color:'#c2410c' }}>
                {expanded ? 'Hide ▲' : 'View ▼'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Show expand button for non-present with unlogged (edge case) ── */}
      {m.status !== 'Present' && m.unloggedStudents.length > 0 && (
        <div className="px-4 pb-3">
          <button onClick={onExpand}
            className="text-[10px] font-bold px-2 py-0.5 rounded-md transition-all hover:opacity-80"
            style={{ background:'#fff7ed', color:'#c2410c' }}>
            {expanded ? 'Hide students ▲' : `Show ${m.unloggedStudents.length} unlogged ▼`}
          </button>
        </div>
      )}

      {/* ── EXPANDED: unlogged student list ── */}
      {expanded && m.unloggedStudents.length > 0 && (
        <div className="at-expand border-t bg-orange-50/60" style={{ borderColor:'#fed7aa' }}>
          <div className="px-4 py-2.5">
            <p className="text-[10px] font-black text-orange-600 uppercase tracking-widest mb-2">
              Pending Class Logs
            </p>
            <div className="space-y-1.5">
              {m.unloggedStudents.map((s, si) => (
                <div key={s.studentId}
                  className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5 border border-orange-100"
                  style={{ animationDelay:`${si * 30}ms` }}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    {/* Mini avatar */}
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black flex-shrink-0"
                      style={{ background:'#fff7ed', color:'#c2410c' }}>
                      {(s.studentName||'').split(' ').map((n: string) => n[0]||'').join('').slice(0,2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-700 truncate">{s.studentName}</p>
                      <p className="text-[10px] text-gray-400">Slot: {slotTo12(s.slotStart)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <div className="w-1.5 h-1.5 rounded-full at-warn" style={{ background:'#f97316' }} />
                    <span className="text-[10px] font-bold" style={{ color:'#c2410c' }}>Not Logged</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── LOADING overlay ── */}
      {busy && (
        <div className="absolute inset-0 bg-white/70 rounded-2xl flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}

/* ── Analytics Tab ───────────────────────────────────────────────────────── */
function AnalyticsTab({ adminPassword, mentorList = [] }: {
  adminPassword: string;
  mentorList?: { mentorId: string; mentorName: string }[];
}) {
  const [data, setData]               = useState<AttendanceAnalytics | null>(null);
  const [loading, setLoading]         = useState(true);
  const [animKey, setAnimKey]         = useState(0);
  const [days, setDays]               = useState(7);
  const [filterMentorId, setFilterMentorId] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    getAttendanceAnalytics(adminPassword, { days, filterMentorId: filterMentorId || undefined })
      .then(r => { setData(r); setAnimKey(k => k + 1); })
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [adminPassword, days, filterMentorId]);

  useEffect(() => { load(); }, [load]);

  const filterActive = days !== 7 || !!filterMentorId;

  return (
    <div className="space-y-4">
      {/* ── Filter Bar ── */}
      <div className="rounded-2xl border border-gray-100 bg-white px-4 py-3 flex items-center gap-3 flex-wrap"
        style={{ boxShadow:'0 2px 8px rgba(0,0,0,.04)' }}>
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex-shrink-0">Filter</p>

        {/* Date range chips */}
        <div className="flex gap-1.5 flex-wrap">
          {([7, 14, 30] as const).map(d => (
            <button key={d} onClick={() => setDays(d)}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all"
              style={days === d
                ? { background:'#312741', color:'#ffe500', boxShadow:'0 2px 6px rgba(49,39,65,.25)' }
                : { background:'#f1f5f9', color:'#64748b' }}>
              {d === 7 ? 'Last 7 days' : d === 14 ? 'Last 14 days' : 'Last 30 days'}
            </button>
          ))}
        </div>

        {/* Mentor dropdown */}
        <div className="relative flex-1 min-w-[140px]">
          <select value={filterMentorId} onChange={e => setFilterMentorId(e.target.value)}
            className="w-full pl-3 pr-8 py-1.5 text-[11px] font-semibold border border-gray-200 rounded-lg outline-none appearance-none bg-white"
            style={{ color: filterMentorId ? '#312741' : '#94a3b8' }}>
            <option value="">All Mentors</option>
            {mentorList.map(m => <option key={m.mentorId} value={m.mentorId}>{m.mentorName}</option>)}
          </select>
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-[10px]">▾</span>
        </div>

        {filterActive && (
          <button onClick={() => { setDays(7); setFilterMentorId(''); }}
            className="text-[10px] font-bold px-2.5 py-1.5 rounded-lg transition-all flex-shrink-0"
            style={{ background:'#fef2f2', color:'#dc2626' }}>
            ✕ Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          <div className="at-skel" style={{ height: 140 }} />
          <div className="grid grid-cols-2 gap-3">
            {[...Array(4)].map((_,i) => <div key={i} className="at-skel" style={{ height: 90 }} />)}
          </div>
          <div className="at-skel" style={{ height: 220 }} />
          <div className="at-skel" style={{ height: 300 }} />
        </div>
      ) : !data ? null : (
        <AnalyticsContent key={animKey} data={data} windowDays={days} />
      )}
    </div>
  );
}

function AnalyticsContent({ data, windowDays }: { data: AttendanceAnalytics; windowDays: number }) {
  const { totals, byDay, byMentor } = data;
  const maxDayTotal = Math.max(...byDay.map(d => d.total), 1);
  const attRate = totals.total ? pct(totals.present, totals.total) : 0;
  const absentRate = totals.total ? pct(totals.absent, totals.total) : 0;
  const leaveRate  = totals.total ? pct(totals.leave,  totals.total) : 0;

  const rateColor = attRate >= 80 ? '#16a34a' : attRate >= 60 ? '#d97706' : '#dc2626';
  const rateGlow  = attRate >= 80 ? 'rgba(22,163,74,.3)' : attRate >= 60 ? 'rgba(217,119,6,.3)' : 'rgba(220,38,38,.3)';

  return (
    <div className="space-y-4">

      {/* ── Hero Rate Card ── */}
      <div className="at-card rounded-2xl overflow-hidden"
        style={{ background:'linear-gradient(135deg,#0f0a1e 0%,#1a1130 40%,#2a1d50 70%,#3d2870 100%)', boxShadow:'0 8px 32px rgba(10,6,28,.45)' }}>
        <div className="px-5 py-5">
          <div className="flex items-center justify-between mb-1">
            <p className="text-white/40 text-[10px] font-black uppercase tracking-widest">{windowDays}-Day Overview</p>
            <p className="text-white/30 text-[10px]">{data.period.from} → {data.period.to}</p>
          </div>

          <div className="flex items-center gap-5 mt-3">
            {/* Big donut */}
            <div className="relative flex-shrink-0" style={{ filter:`drop-shadow(0 0 16px ${rateGlow})` }}>
              <DonutStat val={totals.present} total={totals.total} color={rateColor} size={80} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="font-black leading-none" style={{ color: rateColor, fontSize: 18 }}>{attRate}%</p>
                <p className="text-white/40 text-[9px] font-bold">Present</p>
              </div>
            </div>

            {/* Stats */}
            <div className="flex-1 space-y-2">
              {[
                { label:'Total Sessions', val: totals.total,   bar: 100,       color:'#a78bfa', track:'rgba(167,139,250,.2)' },
                { label:'Present',        val: totals.present, bar: attRate,   color:'#4ade80', track:'rgba(74,222,128,.15)' },
                { label:'Absent',         val: totals.absent,  bar: absentRate, color:'#94a3b8', track:'rgba(148,163,184,.15)' },
                { label:'On Leave',       val: totals.leave,   bar: leaveRate,  color:'#fbbf24', track:'rgba(251,191,36,.15)' },
              ].map(s => (
                <div key={s.label}>
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-white/50 text-[10px] font-medium">{s.label}</p>
                    <p className="font-black text-sm leading-none" style={{ color: s.color }}>{s.val}</p>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: s.track }}>
                    <div className="at-bar h-full rounded-full" style={{ width:`${s.bar}%`, background: s.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Quick stat chips ── */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { icon:'📚', label:'Total Sessions',  val: totals.total,   sub: 'last 7 days',      color:'#7c3aed', bg:'linear-gradient(135deg,#f5f3ff,#ede9fe)', border:'#ddd6fe' },
          { icon:'✅', label:'Attendance Rate',  val: `${attRate}%`,  sub: `${totals.present} present`, color: rateColor, bg:'linear-gradient(135deg,#f0fdf4,#dcfce7)', border:'#bbf7d0' },
          { icon:'⭕', label:'Absent Sessions',  val: totals.absent,  sub: `${absentRate}% of total`, color:'#475569', bg:'linear-gradient(135deg,#f8fafc,#f1f5f9)', border:'#e2e8f0' },
          { icon:'🏖', label:'Leave Sessions',   val: totals.leave,   sub: `${leaveRate}% of total`, color:'#d97706', bg:'linear-gradient(135deg,#fffbeb,#fef3c7)', border:'#fde68a' },
        ].map((c, i) => (
          <div key={i} className="at-card rounded-2xl p-4 border"
            style={{ background:c.bg, borderColor:c.border, animationDelay:`${i*60}ms`, boxShadow:'0 2px 8px rgba(0,0,0,.04)' }}>
            <div className="flex items-start justify-between">
              <p className="text-2xl">{c.icon}</p>
              <p className="font-black text-2xl leading-none" style={{ color:c.color }}>{c.val}</p>
            </div>
            <p className="font-bold text-sm text-gray-700 mt-2">{c.label}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* ── 7-Day Bar Chart ── */}
      <div className="at-card rounded-2xl bg-white border border-gray-100 overflow-hidden"
        style={{ boxShadow:'0 2px 12px rgba(0,0,0,.06)' }}>
        <div className="px-5 pt-4 pb-2 border-b border-gray-50 flex items-center justify-between">
          <div>
            <p className="text-sm font-black text-gray-700">Daily Breakdown</p>
            <p className="text-[10px] text-gray-400">Class sessions per day</p>
          </div>
          <div className="flex items-center gap-3">
            {[['#16a34a','Present'],['#94a3b8','Absent'],['#fbbf24','Leave']].map(([c,l]) => (
              <div key={l} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded" style={{ background:c }} />
                <p className="text-[10px] text-gray-400 font-semibold">{l}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="px-5 pt-4 pb-5">
          <div className="flex items-end gap-2" style={{ height: 140 }}>
            {byDay.map((d, i) => {
              const pH = maxDayTotal > 0 ? Math.max(Math.round((d.present / maxDayTotal) * 116), d.present>0?4:0) : 0;
              const aH = maxDayTotal > 0 ? Math.max(Math.round((d.absent  / maxDayTotal) * 116), d.absent>0?4:0) : 0;
              const lH = maxDayTotal > 0 ? Math.max(Math.round((d.leave   / maxDayTotal) * 116), d.leave>0?4:0) : 0;
              const isToday = d.dateStr === todayIST();
              return (
                <div key={d.dateStr} className="flex-1 flex flex-col items-center min-w-0" style={{ gap:4 }}>
                  {/* Total count */}
                  <p className="text-[9px] font-black mb-1" style={{ color: d.isWeekend?'#cbd5e1':'#64748b' }}>
                    {d.isWeekend ? '—' : d.total || '0'}
                  </p>
                  {/* Stacked bar */}
                  <div className="w-full flex flex-col-reverse overflow-hidden"
                    style={{ height: 116, borderRadius: 8, background:'#f8fafc', gap:1 }}>
                    {d.isWeekend ? (
                      <div className="w-full flex-1 flex items-center justify-center"
                        style={{ background:'repeating-linear-gradient(45deg,#f1f5f9,#f1f5f9 3px,#e9eef5 3px,#e9eef5 8px)' }}>
                        <p className="text-[8px] text-gray-300 font-bold rotate-0">OFF</p>
                      </div>
                    ) : d.total === 0 ? (
                      <div className="w-full flex-1" style={{ background:'#f1f5f9' }} />
                    ) : (
                      <>
                        {pH > 0 && (
                          <div className="at-bar w-full flex-shrink-0" style={{
                            height: pH, animationDelay:`${i*70}ms`,
                            background:'linear-gradient(180deg,#4ade80,#16a34a)',
                            borderRadius: lH===0 && aH===0 ? '6px 6px 0 0' : '0',
                          }} />
                        )}
                        {aH > 0 && (
                          <div className="w-full flex-shrink-0" style={{ height: aH, background:'#cbd5e1' }} />
                        )}
                        {lH > 0 && (
                          <div className="w-full flex-shrink-0" style={{
                            height: lH, background:'linear-gradient(180deg,#fde68a,#f59e0b)',
                            borderRadius: '6px 6px 0 0',
                          }} />
                        )}
                      </>
                    )}
                  </div>
                  {/* Day label */}
                  <div className="text-center mt-1">
                    <p className="text-[10px] font-black" style={{ color: isToday ? '#7c3aed' : d.isWeekend ? '#cbd5e1' : '#475569' }}>
                      {d.dayName}
                    </p>
                    <p className="text-[8px]" style={{ color: isToday ? '#7c3aed' : '#94a3b8' }}>{d.dateStr.slice(5)}</p>
                    {isToday && <div className="w-1 h-1 rounded-full mx-auto mt-0.5" style={{ background:'#7c3aed' }} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Mentor Leaderboard ── */}
      <div className="at-card rounded-2xl overflow-hidden bg-white border border-gray-100" style={{ boxShadow:'0 2px 12px rgba(0,0,0,.06)' }}>
        <div className="px-5 pt-4 pb-3 border-b border-gray-50">
          <p className="text-sm font-black text-gray-700">Mentor Performance</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Ranked by attendance rate · {data.period.from} → {data.period.to}</p>
        </div>

        {byMentor.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <p className="text-3xl mb-2">📋</p>
            <p className="text-sm font-semibold text-gray-500">No class logs found for this period</p>
            <p className="text-xs text-gray-400 mt-1">Data appears once mentors start logging classes</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {byMentor.map((m, i) => {
              const [bgCol, acCol] = pal(m.mentorId);
              const rate = pct(m.present, m.total);
              const barColor = rate >= 80 ? ['#4ade80','#16a34a'] : rate >= 60 ? ['#fde68a','#f59e0b'] : ['#fca5a5','#ef4444'];
              const rankColors = ['#f59e0b','#6b7280','#b45309'];
              const rankBg    = ['#fef3c7','#f3f4f6','#fff7ed'];
              const isTop3 = i < 3;
              return (
                <div key={m.mentorId} className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-gray-50/80">
                  {/* Rank */}
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 font-black text-xs"
                    style={{
                      background: isTop3 ? rankBg[i] : '#f8fafc',
                      color: isTop3 ? rankColors[i] : '#94a3b8',
                    }}>
                    {i < 3 ? ['🥇','🥈','🥉'][i] : i + 1}
                  </div>
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xs font-black flex-shrink-0"
                    style={{ background:`linear-gradient(135deg,${bgCol},${bgCol}cc)`, color:acCol }}>
                    {ini(m.mentorName)}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-sm font-bold text-gray-700 truncate">{m.mentorName}</p>
                      <p className="font-black text-base ml-3 flex-shrink-0" style={{ color: barColor[1] }}>{rate}%</p>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden mb-2" style={{ background:'#f1f5f9' }}>
                      <div className="at-bar h-full rounded-full"
                        style={{
                          width:`${rate}%`,
                          background:`linear-gradient(90deg,${barColor[0]},${barColor[1]})`,
                          animationDelay:`${i * 60}ms`,
                        }} />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background:'#f0fdf4', color:'#16a34a' }}>
                        {m.present} Present
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background:'#f8fafc', color:'#64748b' }}>
                        {m.absent} Absent
                      </span>
                      {m.leave > 0 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background:'#fffbeb', color:'#d97706' }}>
                          {m.leave} Leave
                        </span>
                      )}
                      <span className="text-[10px] text-gray-300 ml-auto">{m.total} total</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────────────────── */
export default function MentorCalendar() {
  const { adminPassword } = useAdminOrCoordinatorSession();

  const [tab, setTab]           = useState<'attendance' | 'analytics'>('attendance');
  const [date, setDate]         = useState(todayIST());
  const [dayName, setDayName]   = useState('');
  const [mentors, setMentors]   = useState<AttendanceMentor[]>([]);
  const [staffOnLeave, setStaffOnLeave] = useState<StaffOnLeave[]>([]);
  const [instLeave, setInstLeave] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [busy, setBusy]         = useState<string | null>(null);
  const [instBusy, setInstBusy] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [animKey, setAnimKey]   = useState(0);
  const [modal, setModal]       = useState<'markInst' | 'cancelInst' | null>(null);
  const [filter, setFilter]     = useState<'all' | St>('all');

  const isToday   = date === todayIST();
  const isFuture  = date > todayIST();
  const isWeekend = ['Sat', 'Sun'].includes(dayName);

  const load = useCallback(async (d: string) => {
    setLoading(true); setExpanded(new Set()); setFilter('all');
    try {
      const r = await getMentorDayAttendance(adminPassword, d);
      setDayName(r.dayName); setMentors(r.mentors); setStaffOnLeave(r.staffOnLeave ?? []);
      setInstLeave(r.instituteLeave); setAnimKey(k => k + 1);
    } catch (e: any) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [adminPassword]);

  useEffect(() => { load(date); }, [date, load]);

  const handleToggle = async (m: AttendanceMentor, next: St) => {
    if (busy) return;
    setBusy(m.mentorId);
    setMentors(prev => prev.map(x => x.mentorId === m.mentorId
      ? { ...x, status: next, unloggedStudents: next !== 'Present' ? [] : x.unloggedStudents }
      : x));
    try {
      if (next === 'MentorLeave') {
        const r = await markMentorLeave(adminPassword, m.mentorId, date);
        toast.success(`${m.mentorName} on leave — ${r.logged} class${r.logged !== 1 ? 'es' : ''} logged`);
      } else {
        await setMentorAttendance(adminPassword, m.mentorId, date, next);
        if (next === 'Present') await load(date);
      }
    } catch (e: any) {
      toast.error(e.message);
      setMentors(prev => prev.map(x => x.mentorId === m.mentorId ? { ...x, status: m.status } : x));
    } finally { setBusy(null); }
  };

  const handleInstLeave = async () => {
    setModal(null); setInstBusy(true);
    try {
      const r = await markInstituteLeave(adminPassword, date);
      toast.success(`Institute Holiday — ${r.logged} class${r.logged !== 1 ? 'es' : ''} auto-logged`);
      await load(date);
    } catch (e: any) { toast.error(e.message); }
    finally { setInstBusy(false); }
  };

  const handleCancelInst = async () => {
    setModal(null); setInstBusy(true);
    try {
      await cancelInstituteLeave(adminPassword, date);
      toast.success('Institute Holiday cancelled');
      await load(date);
    } catch (e: any) { toast.error(e.message); }
    finally { setInstBusy(false); }
  };

  const toggleExpand = useCallback((id: string) => {
    setExpanded(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  const stats = useMemo(() => {
    const present = mentors.filter(m => m.status === 'Present').length;
    const absent  = mentors.filter(m => m.status === 'Absent').length;
    const leave   = mentors.filter(m => m.status === 'MentorLeave').length;
    const warn    = mentors.filter(m => m.status === 'Present' && m.unloggedStudents.length > 0).length;
    const totalStudents = mentors.reduce((a, m) => a + m.scheduledCount, 0);
    const unloggedTotal = mentors.reduce((a, m) => a + (m.status === 'Present' ? m.unloggedStudents.length : 0), 0);
    return { present, absent, leave, warn, totalStudents, unloggedTotal, total: mentors.length };
  }, [mentors]);

  const filtered = useMemo(() =>
    filter === 'all' ? mentors : mentors.filter(m => m.status === filter),
    [mentors, filter]
  );

  return (
    <TrackerLayout>
      <style>{CSS}</style>
      <div className="p-3 md:p-6 space-y-4" style={{ maxWidth: 900, margin: '0 auto' }}>

        {/* ════════════════════════════════════════════════
            HEADER CARD
        ════════════════════════════════════════════════ */}
        <div className="rounded-2xl overflow-hidden"
          style={{ background:'linear-gradient(135deg,#0f0a1e 0%,#1e1433 40%,#2d1f4a 70%,#4a2d72 100%)', boxShadow:'0 8px 32px rgba(10,6,28,.4)' }}>

          {/* Top section */}
          <div className="px-5 pt-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest">Admin Portal</p>
                <h1 className="text-white font-black text-xl tracking-tight mt-0.5">Mentor Attendance</h1>
                {tab === 'attendance' && !isWeekend && !loading && mentors.length > 0 && (
                  <p className="text-white/50 text-xs mt-0.5">
                    {stats.present}/{stats.total} present · {stats.totalStudents} students scheduled
                  </p>
                )}
              </div>

              {/* Date nav — attendance tab only. Tap the date to pick any past day. */}
              {tab === 'attendance' && (
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <div className="flex items-center gap-1 rounded-xl overflow-hidden border border-white/10"
                    style={{ background:'rgba(255,255,255,.07)' }}>
                    <button onClick={() => setDate(d => shiftDate(d, -1))}
                      className="w-8 h-10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all text-xl">
                      ‹
                    </button>
                    <label className="relative text-center px-2 min-w-[96px] cursor-pointer hover:bg-white/10 transition-all py-1 rounded-lg"
                      title="Pick a date">
                      <span className="block text-white font-bold text-xs leading-tight">📅 {fmtShort(date)}</span>
                      {isToday ? (
                        <span className="inline-block text-[9px] font-black px-1.5 py-0.5 rounded-full mt-0.5"
                          style={{ background:'#ffe500', color:'#1e1433' }}>TODAY</span>
                      ) : (
                        <span className="block text-[9px] text-white/40 mt-0.5">tap to change</span>
                      )}
                      {/* Invisible over the label: a tap opens the phone's date picker;
                          on a computer showPicker() opens the calendar */}
                      <input type="date" value={date} max={todayIST()} aria-label="Pick a date"
                        onClick={e => { try { (e.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch {} }}
                        onChange={e => { const v = e.target.value; if (v) setDate(v > todayIST() ? todayIST() : v); }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                    </label>
                    <button onClick={() => setDate(d => shiftDate(d, 1))} disabled={isToday}
                      className="w-8 h-10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all text-xl disabled:opacity-20">
                      ›
                    </button>
                  </div>
                  {!isToday && (
                    <button onClick={() => setDate(todayIST())}
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full transition-all hover:opacity-85"
                      style={{ background:'rgba(255,229,0,.15)', color:'#ffe500' }}>
                      ↺ Back to today
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Stats row — attendance tab */}
            {tab === 'attendance' && !isWeekend && !loading && mentors.length > 0 && (
              <div className="flex gap-2 mt-4 flex-wrap">
                {[
                  { icon:'✅', val:stats.present,  label:'Present',   sub:'' },
                  { icon:'⭕', val:stats.absent,   label:'Absent',    sub:'' },
                  { icon:'🏖', val:stats.leave,    label:'On Leave',  sub:'' },
                  { icon:'⚠', val:stats.warn,     label:'Unlogged',  sub:stats.warn>0 ? `${stats.unloggedTotal} classes` : '' },
                ].map(s => <HeaderStat key={s.label} {...s} />)}
              </div>
            )}
          </div>

          {/* Tab bar */}
          <div className="flex mt-4 border-t border-white/8">
            {(['attendance', 'analytics'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className="flex-1 py-3 text-xs font-bold uppercase tracking-widest transition-all border-b-2"
                style={{
                  color: tab === t ? '#ffe500' : 'rgba(255,255,255,.35)',
                  borderColor: tab === t ? '#ffe500' : 'transparent',
                  background: tab === t ? 'rgba(255,255,255,.05)' : 'transparent',
                }}>
                {t === 'attendance' ? '📋 Attendance' : '📊 Analytics'}
              </button>
            ))}
          </div>

          {/* Institute Leave controls — inside header, attendance tab */}
          {tab === 'attendance' && !isWeekend && !isFuture && !loading && (
            <div className="px-4 py-3">
              {instLeave ? (
                <button onClick={() => setModal('cancelInst')} disabled={instBusy}
                  className="w-full py-2.5 rounded-xl text-xs font-black flex items-center justify-center gap-2 transition-all hover:opacity-85 active:scale-[.98]"
                  style={{ background:'rgba(239,68,68,.18)', color:'#fca5a5', border:'1.5px solid rgba(239,68,68,.3)' }}>
                  🏛 Institute Holiday Active — Tap to Cancel
                </button>
              ) : (
                <button onClick={() => setModal('markInst')} disabled={instBusy}
                  className="w-full py-2.5 rounded-xl text-xs font-black flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-[.98]"
                  style={{ background:'linear-gradient(135deg,#dc2626,#991b1b)', color:'#fff', boxShadow:'0 3px 12px rgba(220,38,38,.4)' }}>
                  {instBusy ? '⏳ Processing…' : '🏛 Mark Institute Holiday'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════
            ANALYTICS TAB
        ════════════════════════════════════════════════ */}
        {tab === 'analytics' && <AnalyticsTab adminPassword={adminPassword} mentorList={mentors.map(m => ({ mentorId: m.mentorId, mentorName: m.mentorName }))} />}

        {/* ════════════════════════════════════════════════
            ATTENDANCE TAB
        ════════════════════════════════════════════════ */}
        {tab === 'attendance' && (
          <>
            {/* Institute Leave Banner */}
            {instLeave && !isWeekend && !loading && (
              <div className="at-banner rounded-2xl px-4 py-3.5 flex items-center gap-3"
                style={{ background:'linear-gradient(135deg,#fef2f2,#fee2e2)', border:'1.5px solid #fecaca' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background:'#dc2626' }}>
                  <span className="text-lg">🏛</span>
                </div>
                <div>
                  <p className="font-black text-red-700 text-sm">Institute Holiday</p>
                  <p className="text-red-400 text-xs mt-0.5">All classes auto-logged as Institute Leave · {fmtLong(date)}</p>
                </div>
              </div>
            )}

            {/* Approved leave — shown on Saturdays too, which are leave days */}
            {!loading && staffOnLeave.length > 0 && <StaffOnLeaveStrip staff={staffOnLeave} date={date} />}

            {/* Weekend */}
            {isWeekend && !loading && (
              <div className="at-card rounded-2xl bg-white border border-gray-100 p-12 text-center"
                style={{ boxShadow:'0 2px 8px rgba(0,0,0,.05)' }}>
                <p className="text-5xl mb-3">🌴</p>
                <p className="font-black text-gray-700 text-lg">Weekend</p>
                <p className="text-gray-400 text-sm mt-1">{dayName} — No classes scheduled</p>
              </div>
            )}

            {/* Future */}
            {isFuture && !loading && (
              <div className="at-card rounded-2xl bg-amber-50 border border-amber-100 p-8 text-center">
                <p className="text-4xl mb-2">📅</p>
                <p className="font-semibold text-amber-700">Future date</p>
                <p className="text-amber-500 text-sm mt-1">Attendance cannot be marked for future dates</p>
              </div>
            )}

            {/* Skeletons */}
            {loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="at-skel" style={{ height: 100, animationDelay:`${i*60}ms` }} />
                ))}
              </div>
            )}

            {/* ── Filter bar ── */}
            {!loading && !isWeekend && mentors.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mr-1">Filter:</p>
                {([
                  { key:'all',         label:'All',      count: stats.total },
                  { key:'Present',     label:'Present',  count: stats.present },
                  { key:'Absent',      label:'Absent',   count: stats.absent },
                  { key:'MentorLeave', label:'On Leave', count: stats.leave },
                ] as const).map(f => (
                  <button key={f.key} onClick={() => setFilter(f.key as any)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all"
                    style={filter === f.key
                      ? { background:'#312741', color:'#ffe500', boxShadow:'0 2px 8px rgba(49,39,65,.25)' }
                      : { background:'#f1f5f9', color:'#64748b' }}>
                    {f.label}
                    <span className="px-1 py-0.5 rounded-full text-[9px]"
                      style={{ background: filter === f.key ? 'rgba(255,229,0,.2)' : '#e2e8f0', color: filter === f.key ? '#ffe500' : '#94a3b8' }}>
                      {f.count}
                    </span>
                  </button>
                ))}
                {/* Summary text */}
                {stats.warn > 0 && (
                  <span className="ml-auto text-[10px] font-bold at-warn" style={{ color:'#c2410c' }}>
                    ⚠ {stats.unloggedTotal} class{stats.unloggedTotal !== 1 ? 'es' : ''} not logged
                  </span>
                )}
              </div>
            )}

            {/* ── Mentor Cards Grid ── */}
            {!loading && !isWeekend && (
              <div key={animKey} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filtered.length === 0 && (
                  <div className="col-span-2 text-center py-12 text-gray-400">
                    {mentors.length === 0 ? 'No active mentors found.' : 'No mentors match this filter.'}
                  </div>
                )}
                {filtered.map((m, idx) => (
                  <div key={m.mentorId} className="relative">
                    <MentorCard
                      m={m} idx={idx}
                      busy={busy === m.mentorId}
                      instLeave={instLeave}
                      isFuture={isFuture}
                      expanded={expanded.has(m.mentorId)}
                      onToggle={next => handleToggle(m, next)}
                      onExpand={() => toggleExpand(m.mentorId)}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Footer summary */}
            {!loading && !isWeekend && mentors.length > 0 && (
              <div className="rounded-2xl px-5 py-3 flex items-center justify-between flex-wrap gap-2"
                style={{ background:'#f8fafc', border:'1px solid #e2e8f0' }}>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span><strong className="text-green-600">{stats.present}</strong> present</span>
                  <span>·</span>
                  <span><strong className="text-gray-500">{stats.absent}</strong> absent</span>
                  <span>·</span>
                  <span><strong className="text-amber-500">{stats.leave}</strong> on leave</span>
                </div>
                <p className="text-[10px] text-gray-400">
                  {stats.totalStudents} students · {stats.totalStudents - stats.unloggedTotal} logged
                  {stats.unloggedTotal > 0 && <span className="text-orange-500 font-bold"> · {stats.unloggedTotal} pending</span>}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ════════════════════════════════════════════════
          MARK INSTITUTE HOLIDAY MODAL
      ════════════════════════════════════════════════ */}
      {modal === 'markInst' && (
        <div className="fixed inset-0 z-[300]">
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={() => setModal(null)} />
          <div className="absolute bg-white rounded-2xl w-[calc(100%-2rem)] max-w-sm overflow-hidden"
            style={{ top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:10, animation:'atFadeUp .25s ease both', boxShadow:'0 24px 64px rgba(0,0,0,.3)' }}>
            <div className="px-6 py-6 text-center"
              style={{ background:'linear-gradient(135deg,#7f1d1d,#dc2626)' }}>
              <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center"
                style={{ background:'rgba(255,255,255,.15)' }}>
                <span className="text-3xl">🏛</span>
              </div>
              <p className="text-white font-black text-lg">Mark Institute Holiday</p>
              <p className="text-white/60 text-xs mt-1">{fmtLong(date)}</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-600 text-center leading-relaxed">
                All <strong>active students' classes</strong> will be auto-logged as <strong>Institute Leave</strong>. All mentors will be marked on leave.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-700 text-center">
                ⚠ This action can be undone by tapping "Cancel Holiday"
              </div>
              <div className="flex gap-3">
                <button onClick={() => setModal(null)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
                  Cancel
                </button>
                <button onClick={handleInstLeave}
                  className="flex-1 py-2.5 rounded-xl text-sm font-black text-white transition-all hover:opacity-90 active:scale-[.97]"
                  style={{ background:'linear-gradient(135deg,#dc2626,#991b1b)' }}>
                  Mark Holiday
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════
          CANCEL INSTITUTE HOLIDAY MODAL
      ════════════════════════════════════════════════ */}
      {modal === 'cancelInst' && (
        <div className="fixed inset-0 z-[300]">
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={() => setModal(null)} />
          <div className="absolute bg-white rounded-2xl w-[calc(100%-2rem)] max-w-sm p-6 space-y-4"
            style={{ top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:10, animation:'atFadeUp .25s ease both', boxShadow:'0 24px 64px rgba(0,0,0,.3)' }}>
            <div className="text-center">
              <p className="text-3xl mb-2">🔄</p>
              <p className="font-black text-gray-800 text-base">Cancel Institute Holiday?</p>
              <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                All auto-logged leave entries will be deleted and mentor attendance reset for this date.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setModal(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
                Keep Holiday
              </button>
              <button onClick={handleCancelInst}
                className="flex-1 py-2.5 rounded-xl text-sm font-black text-white transition-all hover:opacity-90 active:scale-[.97]"
                style={{ background:'linear-gradient(135deg,#d97706,#b45309)' }}>
                Cancel Holiday
              </button>
            </div>
          </div>
        </div>
      )}
    </TrackerLayout>
  );
}
